import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  Grid,
  Box,
  Container,
  Pagination,
  Stack,
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;
const POSTS_PER_PAGE = 5; // Number of posts per page

export default function ActivePosts() {
  const [posts, setPosts] = useState([]);
  const [postLinks, setPostLinks] = useState({});
  const [sessionLinks, setSessionLinks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/posts/active-upcoming/`)
      .then((res) => setPosts(res.data))
      .catch(console.error);
  }, []);

  const handlePostChange = (postId, field, value) => {
    setPostLinks((prev) => ({
      ...prev,
      [postId]: {
        ...prev[postId],
        [field]: value,
      },
    }));
  };

  const handleSessionChange = (sessionId, field, value) => {
    setSessionLinks((prev) => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        [field]: value,
      },
    }));
  };

  const handleSubmitPost = async (postId) => {
    const data = postLinks[postId];
    if (!data?.meeting_link || !data?.host_meeting_link) return;

    try {
      await axios.post(`${API_URL}/api/posts/update-links/`, {
        post_id: postId,
        meeting_link: data.meeting_link,
        host_meeting_link: data.host_meeting_link,
      });
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      console.error('Post update failed:', err);
    }
  };

  const handleSubmitSessions = async (sessions) => {
    const payload = sessions
      .map((s) => {
        const links = sessionLinks[s.id];
        if (links?.meeting_link && links?.host_meeting_link) {
          return {
            session_id: s.id,
            meeting_link: links.meeting_link,
            host_meeting_link: links.host_meeting_link,
          };
        }
        return null;
      })
      .filter(Boolean);

    if (payload.length === 0) return;

    try {
      await axios.post(`${API_URL}/api/sessions/update-links/`, { sessions: payload });

      setPosts((prev) =>
        prev.map((p) => {
          if (!p.is_bundle || !p.sessions) return p;
          return {
            ...p,
            sessions: p.sessions.filter((s) => !payload.find((x) => x.session_id === s.id)),
          };
        })
      );
    } catch (err) {
      console.error('Session update failed:', err);
    }
  };

  // Filter posts by search term
  const filteredPosts = posts.filter((post) =>
    post.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPosts = filteredPosts.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);

  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = Math.min(startIndex + POSTS_PER_PAGE, totalPosts);

  const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh' }}>
      <Header1 />

      <Container maxWidth="md" sx={{ py: 5 }}>
        <Typography variant="h4" gutterBottom align="center">
          Manage Upcoming Posts
        </Typography>

        <TextField
          label="Search by Title"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to page 1 after search
          }}
        />

        {/* ✅ Display total posts */}
        <Typography variant="subtitle2" sx={{ my: 2 }}>
          Showing <strong>{startIndex + 1}</strong> – <strong>{endIndex}</strong> of{' '}
          <strong>{totalPosts}</strong> posts
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {paginatedPosts.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                No posts found.
              </Typography>
            </Grid>
          )}

          {paginatedPosts.map((post) => (
            <Grid item xs={12} key={post.id}>
              <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {post.title || 'Untitled Post'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date: {post.selectedDate} | Time: {post.startTime}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {post.is_bundle && post.sessions?.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Sessions
                    </Typography>

                    {post.sessions
                      .sort(
                        (a, b) =>
                          new Date(`${a.selectedDate}T${a.startTime}`) -
                          new Date(`${b.selectedDate}T${b.startTime}`)
                      )
                      .map((session, index) => (
                        <Paper key={session.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                          <Typography variant="subtitle2">
                            {session.session_title || `Session ${index + 1}`}
                          </Typography>

                          {index === 0 && (
                            <Typography variant="caption" sx={{ color: 'gray' }}>
                              Updating this session will also update the post’s meeting links.
                            </Typography>
                          )}

                          <TextField
                            label="Host Meeting Link"
                            fullWidth
                            margin="dense"
                            value={sessionLinks[session.id]?.host_meeting_link || ''}
                            onChange={(e) =>
                              handleSessionChange(session.id, 'host_meeting_link', e.target.value)
                            }
                          />

                          <TextField
                            label="Attendee Meeting Link"
                            fullWidth
                            margin="dense"
                            value={sessionLinks[session.id]?.meeting_link || ''}
                            onChange={(e) =>
                              handleSessionChange(session.id, 'meeting_link', e.target.value)
                            }
                          />
                        </Paper>
                      ))}

                    <Box sx={{ textAlign: 'right' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSubmitSessions(post.sessions)}
                      >
                        Save Session Links
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <TextField
                      label="Host Meeting Link"
                      fullWidth
                      margin="normal"
                      value={postLinks[post.id]?.host_meeting_link || ''}
                      onChange={(e) =>
                        handlePostChange(post.id, 'host_meeting_link', e.target.value)
                      }
                    />

                    <TextField
                      label="Attendee Meeting Link"
                      fullWidth
                      margin="normal"
                      value={postLinks[post.id]?.meeting_link || ''}
                      onChange={(e) =>
                        handlePostChange(post.id, 'meeting_link', e.target.value)
                      }
                    />

                    <Box sx={{ textAlign: 'right', mt: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSubmitPost(post.id)}
                        disabled={
                          !postLinks[post.id]?.host_meeting_link ||
                          !postLinks[post.id]?.meeting_link
                        }
                      >
                        Save Links
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Stack spacing={2} sx={{ mt: 4, mb: 4 }} alignItems="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
            />
          </Stack>
        )}
      </Container>

      <Footer2 />
    </Box>
  );
}
